import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { formatNumber, kebabCase } from '@makemydeal/dr-common-utils';

import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';

// components
import type { TextInputEventValue } from '@interstate/components/TextInput';
import { Typography } from '@interstate/components/Typography';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// selectors
// consts/enums
import { ODOMETER } from '../../../constants';

// styling
import { DetailContainer } from '../trade.interstate.style';
import { useSingleTradeInFormProvider } from '../MultiTradeIn/TradeInFormProvider';

interface OdometerFieldProps {
    hasTradeIn: boolean;
    isManualFormOpen: boolean;
    disabled?: boolean;
}

export const OdometerField: React.FC<OdometerFieldProps> = ({ hasTradeIn, isManualFormOpen, disabled }) => {
    const { tradeInIndex } = useSingleTradeInFormProvider();
    const dispatch = useDispatch();
    const tradeInVehicle = useSelector(tradeInsSelectors.getTradeInComponentVehicle(tradeInIndex));
    const vehicleMileage = useSelector(tradeInsSelectors.getTradeMileage(tradeInIndex));
    const [mileage, setMileage] = useState(String(vehicleMileage));

    useEffect(() => {
        if (isManualFormOpen) {
            setMileage(vehicleMileage ? String(vehicleMileage) : '');
        }
    }, [isManualFormOpen, vehicleMileage]);

    const onOdometerChange = useCallback((event: InterstateOnChangeEvent<TextInputEventValue>) => {
        const value = event.target.value;
        setMileage(value);
    }, []);

    const onOdometerBlur = useCallback(() => {
        const odometer = document.getElementById('odometer') as HTMLInputElement;
        odometer.value = formatNumber(parseInt(odometer.value));
        dispatch(tradeInsActionCreators.updateTradeInOdometer(Number(mileage), tradeInIndex));
        dispatch(tradeInsActionCreators.updateVehicle({ ...tradeInVehicle, mileage: Number(mileage) }, tradeInIndex));
    }, [dispatch, mileage, tradeInVehicle, tradeInIndex]);

    return (
        <>
            {isManualFormOpen ? (
                <NumericInput
                    size="sm"
                    label={ODOMETER}
                    id="odometer"
                    data-testid="odometer"
                    name={kebabCase(ODOMETER)}
                    onChange={onOdometerChange}
                    onBlur={onOdometerBlur}
                    value={mileage}
                    disabled={disabled}
                    selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                />
            ) : (
                <>
                    {hasTradeIn && (
                        <DetailContainer>
                            <Typography variant="label-md" color="sem.color.on-surface.muted" sx={{ marginBottom: '8px' }}>
                                {ODOMETER}
                            </Typography>
                            <Typography variant="body-md" tag="span" data-testid="trade-odometer-value-label">
                                {formatNumber(vehicleMileage)}
                            </Typography>
                        </DetailContainer>
                    )}
                </>
            )}
        </>
    );
};
